// Chart pictures (full reports): bar, stacked-bar and line charts of an aggregate, drawn as standalone SVG in the
// light print palette from the design tokens (a printed page is white whatever theme the exporter uses), labels in
// their own direction, with the table every chart has as its accessible equivalent. Pure: the browser shows the
// SVG, rasterizes it to PNG for exports, and the same picture goes into the PDF and the DOCX.

import { CHART_WORDS, type ChartWords } from './chart-words';
import type { AggregateBucket, AggregateResult, ChartType } from './charts';
import { CHART_TYPE_LABELS, splitsBars } from './charts';
import { entityFallback, entityPalette } from './design/tokens';
import { textDirection, truncateLabel } from './graph';
import { formatRate, rateTable } from './rates';
import { escapeHtml } from './security';
import { PRINT_COLORS } from './snapshots';

export interface ChartSpec {
  type: ChartType;
  title: string;
  data: AggregateResult;
}

const WIDTH = 760;
const PAD = 20;
const FONT = '"Noto Sans", "Noto Naskh Arabic", "Geeza Pro", Arial, sans-serif';
const esc = escapeHtml;
const n1 = (v: number) => v.toFixed(1);
/** Rough advance of one character at 11 px (layout only: labels are truncated, never measured). */
const CHAR_PX = 6.4;

/** The single-series mark color, and the neutral gray of "Other" (the entity fallback). */
const SERIES = entityPalette[0]?.light ?? entityFallback.light;
const OTHER = entityFallback.light;
/** Categorical sequence for splits: the entity palette's light slots in their fixed order, then gray. */
const splitColor = (i: number, other: boolean) => (other ? OTHER : (entityPalette[i]?.light ?? OTHER));

const isOther = (b: { key: string; label: string }) => /^_*other_*$/i.test(b.key) || b.label.trim().toLowerCase() === 'other';
const isUndated = (b: { key: string; label: string }) =>
  /^_*(undated|none|null)_*$/i.test(b.key) || b.label.trim().toLowerCase() === 'undated';

// ─── Numbers ─────────────────────────────────────────────────────────────────

/** The currency of an amount measure ("Amount (EUR)" → "EUR"), or null for counts. */
export function chartCurrency(measureLabel: string): string | null {
  return /\(([A-Z]{3})\)\s*$/.exec(measureLabel)?.[1] ?? null;
}

function currencySymbol(code: string): string {
  try {
    const sym = new Intl.NumberFormat('en-GB', { style: 'currency', currency: code })
      .formatToParts(0)
      .find(p => p.type === 'currency')?.value;
    return sym ?? code;
  } catch {
    return code;
  }
}

/** "€", "US$", or "AED " (a symbol ending in a letter gets a space before the number). */
const prefix = (currency: string | null) => {
  if (!currency) return '';
  const sym = currencySymbol(currency);
  return /\p{L}$/u.test(sym) ? `${sym} ` : sym;
};

const plain = (v: number, digits: number) => new Intl.NumberFormat('en-GB', { maximumFractionDigits: digits }).format(v);

/** A value in full, en-GB: "1,234" for counts, "€1,234,567.5" for amounts. */
export function formatChartValue(v: number, currency: string | null): string {
  const sign = v < 0 ? '-' : '';
  return `${sign}${prefix(currency)}${plain(Math.abs(v), currency ? 2 : 0)}`;
}

const UNITS: [number, string][] = [
  [1e12, 'tn'],
  [1e9, 'bn'],
  [1e6, 'M'],
  [1e3, 'k'],
];

/** A fraction with up to four decimals, as betweenness is stored: "0.3889", "0.02", "0". */
export const formatDecimal = (v: number) => plain(Math.round(v * 10000) / 10000, 4);

/** A short value for labels and ticks: "950", "12.5k", "€1.2M", "US$3bn". */
export function abbreviateChartValue(v: number, currency: string | null): string {
  const sign = v < 0 ? '-' : '';
  const a = Math.abs(v);
  const round1 = (x: number) => Math.round(x * 10) / 10;
  let i = UNITS.findIndex(([size]) => a >= size);
  // 999,950 rounds to "1M", not "1,000k".
  if (i < 0 && Math.round(a) >= 1000) i = UNITS.length - 1;
  else if (i > 0 && round1(a / (UNITS[i] as [number, string])[0]) >= 1000) i -= 1;
  if (i < 0) return `${sign}${prefix(currency)}${plain(a, a < 10 ? 1 : 0)}`;
  const [size, unit] = UNITS[i] as [number, string];
  const scaled = round1(a / size);
  return `${sign}${prefix(currency)}${plain(scaled >= 100 ? Math.round(scaled) : scaled, 1)}${unit}`;
}

/** Round axis ticks from 0 to at least `max` (about `count` steps of 1, 2, 2.5 or 5 × 10ⁿ; whole steps for counts). */
export function chartTicks(max: number, count = 5, integer = false): number[] {
  if (!(max > 0) || !Number.isFinite(max)) return [0, 1];
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  let step = ([1, 2, 2.5, 5, 10].map(m => m * mag).find(s => s >= raw) ?? 10 * mag) as number;
  if (integer) step = Math.max(1, Math.round(step));
  const n = Math.ceil(max / step - 1e-9);
  return Array.from({ length: n + 1 }, (_, i) => Math.round(i * step * 1e9) / 1e9);
}

// ─── Text ────────────────────────────────────────────────────────────────────

type Align = 'left' | 'right' | 'middle';

/** A label as SVG text in its own direction; `align` is the visual edge (or centre) that sits at `x`. */
function label(
  text: string,
  x: number,
  y: number,
  opts: { size?: number; weight?: number; fill?: string; max?: number; halo?: boolean; align?: Align; italic?: boolean } = {},
) {
  const t = truncateLabel(text, opts.max ?? 32);
  const dir = textDirection(t);
  const align = opts.align ?? 'left';
  const anchor = align === 'middle' ? 'middle' : (align === 'left') === (dir === 'ltr') ? 'start' : 'end';
  return `<text x="${n1(x)}" y="${n1(y)}" direction="${dir}" text-anchor="${anchor}" unicode-bidi="plaintext" dominant-baseline="middle" font-size="${opts.size ?? 11}"${
    opts.weight ? ` font-weight="${opts.weight}"` : ''
  }${opts.italic ? ' font-style="italic"' : ''}${
    opts.halo ? ` paint-order="stroke" stroke="${PRINT_COLORS.background}" stroke-width="3" stroke-linejoin="round"` : ''
  } fill="${opts.fill ?? PRINT_COLORS.foreground}">${esc(t)}</text>`;
}

/** Splits a note into lines of about `max` characters at spaces. */
function wrap(text: string, max: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.trim().split(/\s+/)) {
    if (line && line.length + 1 + word.length > max) {
      lines.push(line);
      line = word;
    } else line = line ? `${line} ${word}` : word;
  }
  if (line) lines.push(line);
  return lines.slice(0, 6);
}

const textWidth = (s: string, size = 11) => [...s].length * CHAR_PX * (size / 11);

// ─── Frame ───────────────────────────────────────────────────────────────────

function frame(h: number, title: string, desc: string, body: string) {
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${WIDTH}" height="${h}" viewBox="0 0 ${WIDTH} ${h}" font-family="${esc(FONT)}" role="img" aria-labelledby="chart-title chart-desc">`,
    `<title id="chart-title">${esc(title)}</title>`,
    `<desc id="chart-desc">${esc(desc)}</desc>`,
    `<rect width="100%" height="100%" fill="${PRINT_COLORS.background}"/>`,
    body,
    '</svg>',
  ].join('\n');
}

/** The note (undisclosed amounts, other currencies…) as muted footnote lines from `y`; returns the markup and its height. */
function footnotes(lines: string[], y: number) {
  const out = lines.map((l, i) => label(l, PAD, y + i * 15, { size: 10, fill: PRINT_COLORS.muted, max: 140 })).join('');
  return { svg: out, height: lines.length * 15 };
}

/** A horizontal bar with a 4 px rounded data end, anchored on the baseline at `x`. */
function hbar(x: number, y: number, w: number, h: number, fill: string, roundEnd = true) {
  if (w <= 0) return '';
  const r = roundEnd ? Math.min(4, w, h / 2) : 0;
  if (!r) return `<rect x="${n1(x)}" y="${n1(y)}" width="${n1(w)}" height="${n1(h)}" fill="${fill}"/>`;
  return `<path d="M${n1(x)} ${n1(y)}h${n1(w - r)}a${r} ${r} 0 0 1 ${r} ${r}v${n1(h - 2 * r)}a${r} ${r} 0 0 1 -${r} ${r}h-${n1(w - r)}z" fill="${fill}"/>`;
}

// ─── Charts ──────────────────────────────────────────────────────────────────

interface Ctx {
  spec: ChartSpec;
  currency: string | null;
  integer: boolean;
  short: (v: number) => string;
  full: (v: number) => string;
  noteLines: string[];
}

function describe(ctx: Ctx, buckets: AggregateBucket[], extra = ''): string {
  const { data, type } = ctx.spec;
  const top = [...buckets]
    .filter(b => !isOther(b))
    .sort((a, b) => b.value - a.value)
    .slice(0, 3)
    .map(b => `${b.label} ${ctx.full(b.value)}`);
  const what = `${CHART_TYPE_LABELS[type]} of ${data.measureLabel} by ${data.groupLabel}, ${buckets.length} ${buckets.length === 1 ? 'bar' : 'bars'}`;
  const lead = type === 'line' ? what.replace(/bars?$/, buckets.length === 1 ? 'point' : 'points') : what;
  return `${lead}.${top.length ? ` Highest: ${top.join('; ')}.` : ''}${extra}${data.note ? ` Note: ${data.note}` : ''}`;
}

function emptyChart(ctx: Ctx) {
  const top = 44;
  const boxH = 90;
  const notes = footnotes(ctx.noteLines, top + boxH + 20);
  const h = top + boxH + (notes.height ? notes.height + 16 : 0) + PAD;
  const body = [
    label(ctx.spec.title, PAD, 24, { size: 15, weight: 600, max: 90 }),
    `<rect x="${PAD}" y="${top}" width="${WIDTH - 2 * PAD}" height="${boxH}" rx="4" fill="${PRINT_COLORS.background}" stroke="${PRINT_COLORS.grid}" stroke-width="1"/>`,
    label('Nothing to chart for this question.', WIDTH / 2, top + boxH / 2, {
      size: 12,
      fill: PRINT_COLORS.muted,
      align: 'middle',
      max: 60,
    }),
    notes.svg,
  ].join('');
  return { svg: frame(h, ctx.spec.title, `${ctx.spec.title}. Nothing to chart for this question.`, body), width: WIDTH, height: h };
}

function barChart(ctx: Ctx, mode: 'single' | 'stacked' | 'grouped') {
  const { data } = ctx.spec;
  const buckets = data.buckets;
  const stacked = mode !== 'single';
  const grouped = mode === 'grouped';
  const splits = stacked ? data.splits : [];
  const totals = buckets.map(b => (stacked && b.split ? b.split.reduce((s, v) => s + Math.max(0, v), 0) : Math.max(0, b.value)));
  // Grouped bars share one scale over the largest single value; stacked bars over the largest total.
  const axisOver = grouped ? Math.max(...buckets.flatMap(b => (b.split ?? []).map(v => Math.max(0, v))), 0) : Math.max(...totals, 0);
  const ticks = chartTicks(axisOver, 5, ctx.integer);
  const axisMax = ticks[ticks.length - 1] as number;

  const labelW = Math.min(240, Math.max(80, Math.max(...buckets.map(b => textWidth(truncateLabel(b.label, 34)))) + 4));
  const valueW = Math.min(120, Math.max(40, Math.max(...totals.map(v => textWidth(ctx.short(v), 10))) + 10));
  const x0 = PAD + labelW + 10;
  const plotW = WIDTH - PAD - valueW - x0;
  const x = (v: number) => x0 + (Math.max(0, v) / (axisMax || 1)) * plotW;

  // Legend (stacked): swatch + label, wrapped onto rows.
  let y = 44;
  const legend: string[] = [];
  if (stacked && splits.length) {
    let lx = PAD;
    for (let i = 0; i < splits.length; i++) {
      const s = splits[i] as { key: string; label: string };
      const text = truncateLabel(s.label, 24);
      const w = 12 + 6 + textWidth(text) + 16;
      if (lx + w > WIDTH - PAD && lx > PAD) {
        lx = PAD;
        y += 18;
      }
      legend.push(
        `<rect x="${n1(lx)}" y="${n1(y - 5)}" width="10" height="10" rx="2" fill="${splitColor(i, isOther(s))}"/>`,
        label(text, lx + 16, y, { size: 11, max: 24 }),
      );
      lx += w;
    }
    y += 22;
  }
  // Axis titles: the group above the label column, the measure under the axis.
  const groupTitleY = y;
  y += 18;
  // Grouped: one thin bar per split value under the bucket's label; the row grows with the splits.
  const groupBarH = 9;
  const rowH = grouped ? Math.max(24, splits.length * (groupBarH + 2) + 10) : 24;
  const barH = 14;
  const plotTop = y;
  const plotBottom = plotTop + buckets.length * rowH;
  const grid = ticks
    .map(
      t =>
        `<line x1="${n1(x(t))}" y1="${plotTop - 4}" x2="${n1(x(t))}" y2="${plotBottom + 2}" stroke="${PRINT_COLORS.grid}" stroke-width="1"/>${label(
          ctx.short(t),
          x(t),
          plotBottom + 14,
          { size: 10, fill: PRINT_COLORS.muted, align: 'middle', max: 14 },
        )}`,
    )
    .join('');
  const rows = buckets
    .map((b, i) => {
      const cy = plotTop + i * rowH + rowH / 2;
      const other = isOther(b);
      const name = label(b.label, x0 - 10, cy, {
        align: 'right',
        max: 34,
        fill: other ? PRINT_COLORS.muted : PRINT_COLORS.foreground,
        italic: other,
      });
      let marks = '';
      if (grouped && b.split) {
        const top = cy - (splits.length * (groupBarH + 2)) / 2;
        marks = b.split
          .map((raw, k) => {
            const v = Math.max(0, raw);
            const sp = splits[k];
            const by = top + k * (groupBarH + 2);
            return (
              hbar(x0, by, x(v) - x0, groupBarH, splitColor(k, sp ? isOther(sp) : false)) +
              (v > 0 ? label(ctx.short(v), x(v) + 4, by + groupBarH / 2, { size: 8.5, fill: PRINT_COLORS.muted, max: 16 }) : '')
            );
          })
          .join('');
      } else if (stacked && b.split) {
        let acc = 0;
        const segs = b.split.map((v, k) => ({ v: Math.max(0, v), k })).filter(s => s.v > 0);
        marks = segs
          .map((s, j) => {
            const a = x(acc);
            acc += s.v;
            const last = j === segs.length - 1;
            // A 2 px surface gap between adjacent segments.
            const w = x(acc) - a - (last ? 0 : 2);
            const sp = splits[s.k];
            return hbar(a, cy - barH / 2, w, barH, splitColor(s.k, sp ? isOther(sp) : false), last);
          })
          .join('');
      } else {
        marks = hbar(x0, cy - barH / 2, x(totals[i] as number) - x0, barH, other ? OTHER : SERIES);
      }
      const value = grouped
        ? ''
        : label(ctx.short(totals[i] as number), x(totals[i] as number) + 6, cy, {
            size: 10,
            fill: PRINT_COLORS.foreground,
            max: 16,
          });
      return name + marks + value;
    })
    .join('');
  const axis = `<line x1="${n1(x0)}" y1="${plotTop - 4}" x2="${n1(x0)}" y2="${plotBottom + 2}" stroke="${PRINT_COLORS.line}" stroke-width="1"/>`;
  const measureY = plotBottom + 34;
  const titles =
    label(data.groupLabel, x0 - 10, groupTitleY, { size: 10, weight: 600, fill: PRINT_COLORS.muted, align: 'right', max: 34 }) +
    label(data.measureLabel, x0 + plotW / 2, measureY, { size: 10, weight: 600, fill: PRINT_COLORS.muted, align: 'middle', max: 60 });
  const notes = footnotes(ctx.noteLines, measureY + 24);
  const h = Math.ceil(measureY + (notes.height ? 24 + notes.height - 6 : 8) + PAD - 8);
  const body = [
    label(ctx.spec.title, PAD, 24, { size: 15, weight: 600, max: 90 }),
    legend.join(''),
    grid,
    axis,
    rows,
    titles,
    notes.svg,
  ].join('');
  const extra = stacked && splits.length ? ` ${grouped ? 'Grouped' : 'Split'} by ${splits.map(s => s.label).join(', ')}.` : '';
  return { svg: frame(h, ctx.spec.title, `${ctx.spec.title}. ${describe(ctx, buckets, extra)}`, body), width: WIDTH, height: h };
}

function lineChart(ctx: Ctx) {
  const { data } = ctx.spec;
  const undated = data.buckets.filter(isUndated);
  const points = data.buckets.filter(b => !isUndated(b));
  const ticks = chartTicks(Math.max(...points.map(b => b.value), 0), 5, ctx.integer);
  const axisMax = ticks[ticks.length - 1] as number;
  const tickW = Math.max(...ticks.map(t => textWidth(ctx.short(t), 10)));
  const x0 = PAD + 18 + tickW + 8;
  const x1 = WIDTH - PAD - 24;
  const plotTop = 60;
  const plotH = 240;
  const plotBottom = plotTop + plotH;
  const step = points.length > 1 ? (x1 - x0 - 24) / (points.length - 1) : 0;
  const px = (i: number) => (points.length > 1 ? x0 + 12 + i * step : (x0 + x1) / 2);
  const py = (v: number) => plotBottom - (Math.max(0, v) / (axisMax || 1)) * plotH;

  const grid = ticks
    .map(
      t =>
        `<line x1="${n1(x0)}" y1="${n1(py(t))}" x2="${n1(x1)}" y2="${n1(py(t))}" stroke="${t === 0 ? PRINT_COLORS.line : PRINT_COLORS.grid}" stroke-width="1"/>${label(
          ctx.short(t),
          x0 - 6,
          py(t),
          { size: 10, fill: PRINT_COLORS.muted, align: 'right', max: 14 },
        )}`,
    )
    .join('');
  // Bucket labels: every k-th when they would collide.
  const maxLabel = Math.max(...points.map(b => textWidth(truncateLabel(b.label, 12), 10)), 1);
  const every = step > 0 ? Math.max(1, Math.ceil((maxLabel + 8) / step)) : 1;
  const xLabels = points
    .map((b, i) =>
      i % every === 0 || i === points.length - 1
        ? label(b.label, px(i), plotBottom + 14, { size: 10, fill: PRINT_COLORS.muted, align: 'middle', max: 12 })
        : '',
    )
    .join('');
  const path =
    points.length > 1
      ? `<path d="${points.map((b, i) => `${i ? 'L' : 'M'}${n1(px(i))} ${n1(py(b.value))}`).join('')}" fill="none" stroke="${SERIES}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>`
      : '';
  // Value labels at the points: all of them up to 12 points, else the first, last, highest and lowest.
  const values = points.map(b => b.value);
  const hi = values.indexOf(Math.max(...values));
  const lo = values.indexOf(Math.min(...values));
  const labelled = (i: number) => points.length <= 12 || i === 0 || i === points.length - 1 || i === hi || i === lo;
  const dots = points
    .map((b, i) => {
      const fill = isOther(b) ? OTHER : SERIES;
      const dot = `<circle cx="${n1(px(i))}" cy="${n1(py(b.value))}" r="4" fill="${fill}" stroke="${PRINT_COLORS.background}" stroke-width="2"/>`;
      return labelled(i)
        ? dot + label(ctx.short(b.value), px(i), py(b.value) - 12, { size: 10, align: 'middle', halo: true, max: 16 })
        : dot;
    })
    .join('');
  const titles =
    label(data.measureLabel, PAD, plotTop - 16, { size: 10, weight: 600, fill: PRINT_COLORS.muted, max: 60 }) +
    label(data.groupLabel, (x0 + x1) / 2, plotBottom + 34, { size: 10, weight: 600, fill: PRINT_COLORS.muted, align: 'middle', max: 60 });
  const undatedLines = undated.map(b => `${b.label}: ${ctx.full(b.value)} (not on the line).`);
  const notes = footnotes([...undatedLines, ...ctx.noteLines], plotBottom + 58);
  const h = Math.ceil(plotBottom + 34 + (notes.height ? 24 + notes.height - 6 : 8) + PAD - 8);
  const body = [label(ctx.spec.title, PAD, 24, { size: 15, weight: 600, max: 90 }), grid, xLabels, path, dots, titles, notes.svg].join('');
  const range = points.length ? ` From ${points[0]?.label} to ${points[points.length - 1]?.label}.` : '';
  const extra = range + undated.map(b => ` ${b.label}: ${ctx.full(b.value)}.`).join('');
  return { svg: frame(h, ctx.spec.title, `${ctx.spec.title}. ${describe(ctx, points, extra)}`, body), width: WIDTH, height: h };
}

function context(spec: ChartSpec): Ctx {
  const currency = chartCurrency(spec.data.measureLabel);
  const rate = spec.data.rate;
  if (rate) {
    // Rates: values as percentages or per entity; the base rate and the method print under the picture.
    const base =
      rate.kind === 'share'
        ? spec.data.splits.map((sp, k) => `${sp.label} ${formatRate(rate.base[k] ?? 0, 'share')}`).join(', ')
        : `${formatRate(rate.base[0] ?? 0, 'per_entity')} per entity`;
    const lines = [`Base rate, all ${rate.population.toLocaleString('en-GB')} matched entities: ${base}.`];
    if (spec.data.note) lines.push(spec.data.note);
    if (spec.data.method) lines.push(spec.data.method);
    return {
      spec,
      currency: null,
      integer: false,
      short: v => formatRate(v, rate.kind),
      full: v => formatRate(v, rate.kind),
      noteLines: lines.flatMap(l => wrap(l, 135)).slice(0, 8),
    };
  }
  if (spec.data.format === 'decimal') {
    // Fractions (betweenness): four decimals; the note and the method print under the picture.
    const lines = [spec.data.note, spec.data.method].filter((l): l is string => !!l);
    return {
      spec,
      currency: null,
      integer: false,
      short: v => formatDecimal(v),
      full: v => formatDecimal(v),
      noteLines: lines.flatMap(l => wrap(l, 135)).slice(0, 8),
    };
  }
  return {
    spec,
    currency,
    integer: !currency,
    short: v =>
      currency ? abbreviateChartValue(v, currency) : Math.abs(v) < 10_000 ? formatChartValue(v, null) : abbreviateChartValue(v, null),
    full: v => formatChartValue(v, currency),
    noteLines: spec.data.note ? wrap(spec.data.note, 135) : [],
  };
}

/** The chart as a standalone SVG in the print palette (labels in their own direction), with its size in pixels. */
export function chartSvg(spec: ChartSpec): { svg: string; width: number; height: number } {
  const ctx = context(spec);
  const buckets = spec.data.buckets;
  const drawable = spec.type === 'line' ? buckets.filter(b => !isUndated(b)) : buckets;
  if (!drawable.length || drawable.every(b => !(b.value > 0) && !(b.split ?? []).some(v => v > 0))) return emptyChart(ctx);
  if (spec.type === 'line') return lineChart(ctx);
  const split = splitsBars(spec.type) && spec.data.splits.length > 0;
  return barChart(ctx, !split ? 'single' : spec.type === 'grouped_bar' ? 'grouped' : 'stacked');
}

/** The chart's data as a table (the accessible equivalent, and what a document may print under it). */
export function chartTable(spec: ChartSpec, words: ChartWords = CHART_WORDS.en): { headers: string[]; rows: string[][] } {
  const { data, type } = spec;
  // Rates: every subgroup figure beside the base rate of all matched entities, and the group size.
  if (data.rate) return rateTable({ ...data, rate: data.rate }, words);
  const full = (v: number) => (data.format === 'decimal' ? formatDecimal(v) : formatChartValue(v, chartCurrency(data.measureLabel)));
  if (splitsBars(type) && data.splits.length) {
    const headers = [data.groupLabel, ...data.splits.map(s => s.label), words.total];
    const rows = data.buckets.map(b => [b.label, ...data.splits.map((_, k) => full(b.split?.[k] ?? 0)), full(b.value)]);
    if (data.buckets.length)
      rows.push([
        words.total,
        ...data.splits.map((_, k) => full(data.buckets.reduce((s, b) => s + (b.split?.[k] ?? 0), 0))),
        full(data.buckets.reduce((s, b) => s + b.value, 0)),
      ]);
    return { headers, rows };
  }
  const rows = data.buckets.map(b => [b.label, full(b.value)]);
  if (type !== 'line' && data.format !== 'decimal' && data.buckets.length)
    rows.push([words.total, full(data.buckets.reduce((s, b) => s + b.value, 0))]);
  return { headers: [data.groupLabel, data.measureLabel], rows };
}
